<script setup lang="ts">
import type { ItemLoginHistory } from '@/types/UserHistory';

// Histórico de login do Minha Conta V2 (ENG-362).
// Figma: mobile 19771:42200 / desktop 19771:52395.
// Dados reais: mesma API da página antiga pages/user/login-history.vue
// (useUserHistory → GET /bff/users/login-history).
const { getLoginHistory, loginHistory, loadingUserHistory } = useUserHistory();

const items = computed<ItemLoginHistory[]>(
  () => loginHistory.value?.data || [],
);

function formatDate(date: string) {
  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) {
    return date;
  }

  return parsed.toLocaleString('pt-BR');
}

function locationLabel(item: ItemLoginHistory) {
  if (!item.city && !item.state) {
    return 'Localização não encontrada';
  }

  return [item.city, item.state].filter(Boolean).join(' / ');
}

function hasLocation(item: ItemLoginHistory) {
  return !!(item.city || item.state);
}

function changePage(page: number) {
  getLoginHistory(page);
}

onMounted(() => {
  getLoginHistory();
});
</script>

<template>
  <!-- desktop: o box ocupa a altura toda e SÓ a lista rola (paginação fixa) -->
  <div
    class="border-solid flex flex-col gap-[16px] md:h-full md:min-h-0 md:rounded-[24px] md:border md:border-v2-default/40 md:p-[16px]"
  >
    <div
      v-if="loadingUserHistory"
      class="flex flex-col gap-[10px] md:min-h-0 md:flex-1 md:overflow-hidden"
    >
      <div
        v-for="n in 8"
        :key="`login-skeleton-${n}`"
        class="login-history-skeleton h-[56px] w-full rounded-[10px] bg-v2-default/40"
      />
    </div>

    <template v-else>
      <div
        v-if="items.length"
        class="user-v2-scroll flex flex-col gap-[10px] md:min-h-0 md:flex-1 md:overflow-y-auto md:pr-[6px]"
      >
        <UserV2HistoryCard
          v-for="(item, index) in items"
          :key="`${item.date}-${index}`"
        >
          <span
            class="flex size-[32px] shrink-0 items-center justify-center rounded-[10px] bg-v2-default/40 text-white"
          >
            <!-- localização encontrada = pin; senão globo -->
            <svg
              class="size-[20px]"
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1.5"
              viewBox="0 0 24 24"
            >
              <path
                v-if="hasLocation(item)"
                d="M12 21s-7-5.4-7-11a7 7 0 0 1 14 0c0 5.6-7 11-7 11Zm0-8.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"
              />
              <path
                v-else
                d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Zm-9 9h18M12 3c2.5 2.4 3.8 5.6 3.8 9s-1.3 6.6-3.8 9c-2.5-2.4-3.8-5.6-3.8-9s1.3-6.6 3.8-9Z"
              />
            </svg>
          </span>

          <span class="flex min-w-0 flex-1 flex-col">
            <span
              class="truncate text-[14px] font-medium leading-[20px] text-white"
            >
              {{ formatDate(item.date) }}
            </span>
            <span
              class="truncate text-[12px] leading-[16px] text-v2-default-800"
            >
              {{ locationLabel(item) }}
            </span>
          </span>

          <template #details>
            <div class="flex flex-col gap-[4px]">
              <span class="text-[12px] leading-[16px] text-v2-default-800">
                Endereço IP
              </span>
              <span class="break-all text-[12px] leading-[16px] text-white">
                {{ item.ip || '-' }}
              </span>
            </div>

            <div class="flex flex-col gap-[4px]">
              <span class="text-[12px] leading-[16px] text-v2-default-800">
                Coordenadas
              </span>
              <div class="flex gap-[16px] text-[12px] leading-[16px]">
                <span class="text-v2-default-800">
                  Lat:
                  <span class="text-white">{{ item.latitude || '-' }}</span>
                </span>
                <span class="text-v2-default-800">
                  Long:
                  <span class="text-white">{{ item.longitude || '-' }}</span>
                </span>
              </div>
            </div>
          </template>
        </UserV2HistoryCard>
      </div>

      <p
        v-else
        class="rounded-[10px] bg-v2-default/40 p-[16px] text-center text-[12px] leading-[16px] text-v2-default-800"
      >
        Nenhum registro encontrado.
      </p>

      <UserV2Pagination
        v-if="(loginHistory?.last_page || 1) > 1"
        :last-page="loginHistory?.last_page || 1"
        :page="loginHistory?.current_page || 1"
        @change="changePage"
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
// main.scss reseta `transition` — skeleton pulsa via keyframes
.login-history-skeleton {
  animation: login-history-pulse 1.2s ease-in-out infinite;
}

@keyframes login-history-pulse {
  0%,
  100% {
    opacity: 0.45;
  }

  50% {
    opacity: 1;
  }
}
</style>
